/**
 * Trust badge definitions and property-level computation.
 * Badges are derived from host verification, amenities, ratings, and listing metadata.
 */

export const TRUST_BADGE_DEFS = {
  verified_property: {
    id: 'verified_property',
    label: 'Verified Property',
    shortLabel: 'Verified',
    category: 'trust',
    color: '#0d9488',
    bg: '#ccfbf1',
    description: 'Listed and reviewed by UBSAAPP with verified host ownership.',
  },
  verified_photos: {
    id: 'verified_photos',
    label: 'Verified Photos',
    shortLabel: 'Real Photos',
    category: 'trust',
    color: '#2563eb',
    bg: '#dbeafe',
    description: 'Photos uploaded by the host — not stock images.',
  },
  book_stay_guarantee: {
    id: 'book_stay_guarantee',
    label: 'Book & Stay Guarantee',
    shortLabel: 'Guaranteed',
    category: 'trust',
    color: '#7c3aed',
    bg: '#ede9fe',
    description: 'Protected booking with UBSAAPP support if issues arise.',
  },
  super_host: {
    id: 'super_host',
    label: 'Super Host',
    shortLabel: 'Super Host',
    category: 'host',
    color: '#ea580c',
    bg: '#ffedd5',
    description: 'Highly rated host with consistent guest satisfaction.',
  },
  family_friendly: {
    id: 'family_friendly',
    label: 'Family Friendly',
    shortLabel: 'Family',
    category: 'safety',
    color: '#0891b2',
    bg: '#cffafe',
    description: 'Amenities suited for families traveling together.',
  },
  women_friendly: {
    id: 'women_friendly',
    label: 'Women Friendly',
    shortLabel: 'Women Safe',
    category: 'safety',
    color: '#db2777',
    bg: '#fce7f3',
    description: 'Security features and policies for solo women travelers.',
  },
  child_friendly: {
    id: 'child_friendly',
    label: 'Child Friendly',
    shortLabel: 'Kids',
    category: 'safety',
    color: '#65a30d',
    bg: '#ecfccb',
    description: 'Crib, high chair, or child-friendly amenities available.',
  },
  long_stay_friendly: {
    id: 'long_stay_friendly',
    label: 'Long Stay Friendly',
    shortLabel: 'Long Stay',
    category: 'comfort',
    color: '#4f46e5',
    bg: '#e0e7ff',
    description: 'Suitable for extended stays of a week or more.',
  },
  safe_neighborhood: {
    id: 'safe_neighborhood',
    label: 'Safe Neighborhood',
    shortLabel: 'Safe Area',
    category: 'safety',
    color: '#059669',
    bg: '#d1fae5',
    description: 'Located near essential services with safety amenities.',
  },
  security_24x7: {
    id: 'security_24x7',
    label: '24×7 Security',
    shortLabel: '24×7 Security',
    category: 'safety',
    color: '#1e40af',
    bg: '#dbeafe',
    description: 'CCTV and/or security guard on premises.',
  },
  free_parking: {
    id: 'free_parking',
    label: 'Free Parking',
    shortLabel: 'Parking',
    category: 'comfort',
    color: '#475569',
    bg: '#f1f5f9',
    description: 'Complimentary parking available on site.',
  },
  high_speed_wifi: {
    id: 'high_speed_wifi',
    label: 'High-Speed Wi-Fi',
    shortLabel: 'Wi-Fi',
    category: 'comfort',
    color: '#0284c7',
    bg: '#e0f2fe',
    description: 'Reliable internet for work and streaming.',
  },
  customer_support_24x7: {
    id: 'customer_support_24x7',
    label: '24×7 Customer Support',
    shortLabel: '24×7 Support',
    category: 'trust',
    color: '#b45309',
    bg: '#fef3c7',
    description: 'UBSAAPP support available around the clock.',
  },
};

/** Safety filter options for search sidebar */
export const SAFETY_FILTER_OPTIONS = [
  { id: 'verified_property', label: 'Verified Property', group: 'trust' },
  { id: 'verified_photos', label: 'Verified Photos', group: 'trust' },
  { id: 'book_stay_guarantee', label: 'Book & Stay Guarantee', group: 'trust' },
  { id: 'super_host', label: 'Super Host', group: 'host' },
  { id: 'family_friendly', label: 'Family Friendly', group: 'safety' },
  { id: 'women_friendly', label: 'Women Friendly', group: 'safety' },
  { id: 'child_friendly', label: 'Child Friendly', group: 'safety' },
  { id: 'long_stay_friendly', label: 'Long Stay Friendly', group: 'comfort' },
  { id: 'safe_neighborhood', label: 'Safe Neighborhood', group: 'safety' },
  { id: 'security_24x7', label: '24×7 Security', group: 'safety' },
  { id: 'free_parking', label: 'Free Parking', group: 'comfort' },
  { id: 'high_speed_wifi', label: 'High-Speed Wi-Fi', group: 'comfort' },
];

export const GUARANTEE_ITEMS = [
  {
    id: 'verified_property',
    title: 'Verified Property',
    description: 'Every listed property is reviewed and linked to a verified host.',
  },
  {
    id: 'verified_photos',
    title: 'Verified Photos',
    description: 'What you see is what you get — real photos from the host.',
  },
  {
    id: 'final_price',
    title: 'Final Price (No Hidden Charges)',
    description: 'Transparent pricing with taxes and fees shown upfront.',
  },
  {
    id: 'booking_honored',
    title: 'Booking Honored',
    description: 'Your confirmed reservation is protected — we step in if issues arise.',
  },
  {
    id: 'stay_support',
    title: 'Stay Support',
    description: '24×7 UBSAAPP support during your entire stay.',
  },
];

const HOST_SOURCES = new Set(['host', 'internal', 'manual']);

const norm = (v) => String(v || '').toLowerCase().trim();

const hasAmenity = (amenities, ...patterns) => {
  const list = (amenities || []).map(norm);
  return patterns.some((p) => list.some((a) => a.includes(norm(p))));
};

const hasHouseRule = (rules, ...patterns) => {
  const list = (rules || []).map(norm);
  return patterns.some((p) => list.some((r) => r.includes(norm(p))));
};

export const isHostUploadedProperty = (property) => {
  if (!property) return false;
  const src = norm(property.originSource || property.source);
  if (HOST_SOURCES.has(src)) return true;
  return Boolean(property.hostId);
};

const hasVerifiedPhotos = (property) => {
  if (property?.hasRealImages) return true;
  const images = property?.images || [];
  if (images.length < 1) return false;
  const placeholders = ['placeholder', 'unsplash.com/photo-1501117716987', 'via.placeholder'];
  const realCount = images.filter((url) => {
    const u = norm(url);
    return u && !placeholders.some((p) => u.includes(p));
  }).length;
  return realCount >= 1;
};

const isVerifiedHost = (property) =>
  Boolean(
    property?.hostProfileComplete ||
      property?.hostVerified ||
      property?.hostId?.hostProfileComplete ||
      property?.hostId?.isVerified
  );

/**
 * Compute applicable trust badge IDs for a property.
 */
export const computeTrustBadges = (property) => {
  if (!property) return [];

  const badges = [];
  const amenities = property.amenities || [];
  const houseRules = property.houseRules || [];
  const hostUploaded = isHostUploadedProperty(property);
  const active = property.status !== 'inactive';

  if (hostUploaded && active) {
    badges.push('verified_property', 'book_stay_guarantee', 'customer_support_24x7');
  }

  if (hasVerifiedPhotos(property)) {
    badges.push('verified_photos');
  }

  const rating = Number(property.rating) || 0;
  const reviews = Number(property.reviews) || 0;
  if ((rating >= 4.5 && reviews >= 5) || isVerifiedHost(property)) {
    badges.push('super_host');
  }

  if (hasAmenity(amenities, 'crib', 'high chair', 'kids toys', "children's books", 'baby bath')) {
    badges.push('family_friendly', 'child_friendly');
  }

  if (
    (hasAmenity(amenities, 'cctv', 'security guard', 'safe', 'smoke detector') && rating >= 3.5) ||
    hasHouseRule(houseRules, 'women', 'female', 'solo')
  ) {
    badges.push('women_friendly');
  }

  const maxStay = Number(property.maxStayNights) || 0;
  if (maxStay >= 28 || hasAmenity(amenities, 'washing machine', 'laundry', 'kitchen', 'dedicated workspace')) {
    badges.push('long_stay_friendly');
  }

  const nearby = property.nearbyPlaces || [];
  const hasSafeNearby = nearby.some((p) =>
    /police|hospital|metro|market|mall|station/i.test(`${p.name || ''} ${p.category || ''}`)
  );
  if (hasSafeNearby || hasAmenity(amenities, 'cctv', 'security guard', 'smoke detector', 'first aid')) {
    badges.push('safe_neighborhood');
  }

  if (hasAmenity(amenities, 'cctv', 'security guard')) {
    badges.push('security_24x7');
  }

  if (hasAmenity(amenities, 'free parking')) {
    badges.push('free_parking');
  }

  if (hasAmenity(amenities, 'wifi', 'fast wifi')) {
    badges.push('high_speed_wifi');
  }

  return [...new Set(badges)];
};

export const propertyHasTrustBadge = (property, badgeId) =>
  computeTrustBadges(property).includes(badgeId);

export const propertyMatchesSafetyFilters = (property, selectedFilters) => {
  if (!selectedFilters?.length) return true;
  const badgeSet = new Set(computeTrustBadges(property));
  return selectedFilters.every((id) => badgeSet.has(id));
};

export const getTrustBadgeDef = (id) => TRUST_BADGE_DEFS[id] || null;

export const getTopTrustBadges = (property, limit = 4) => {
  const ids = computeTrustBadges(property);
  const priority = [
    'book_stay_guarantee',
    'verified_property',
    'verified_photos',
    'super_host',
    'women_friendly',
    'family_friendly',
    'security_24x7',
    'safe_neighborhood',
    'high_speed_wifi',
    'free_parking',
    'customer_support_24x7',
  ];
  const sorted = [...ids].sort((a, b) => {
    const ai = priority.indexOf(a);
    const bi = priority.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  return sorted.slice(0, limit).map((id) => TRUST_BADGE_DEFS[id]).filter(Boolean);
};
